/**
 * LinksTile — sources or results to open (papers, listings, bookings).
 *
 * data: { links: { title: string; url: string; note? }[] }
 *
 * Each row shows the host, so a person sees where a link goes before
 * opening it; non-http(s) URLs render as plain text.
 */
import { ExternalLink } from "lucide-react";
import { hostOf, safeHttpUrl } from "@/lib/safe-url";
import type { TileBodyProps } from "../registry";

interface Link {
  title: string;
  url: string;
  note?: string;
}

export function LinksTile({ data }: TileBodyProps) {
  const links = (data as { links?: Link[] }).links ?? [];
  if (!links.length) return <p className="text-[12.5px] text-fg-faint">No links.</p>;
  return (
    <ul className="space-y-1.5 text-[13px]">
      {links.map((l, i) => {
        const href = safeHttpUrl(l.url);
        return (
          <li key={i} className="border-b border-border pb-1.5 last:border-b-0">
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group/link flex items-baseline gap-1.5 text-fg hover:text-accent"
              >
                <span className="font-medium underline-offset-2 group-hover/link:underline">
                  {l.title}
                </span>
                <ExternalLink size={10} className="flex-shrink-0 text-fg-faint" />
                <span className="ml-auto flex-shrink-0 font-mono text-[10px] text-fg-faint">
                  {hostOf(href)}
                </span>
              </a>
            ) : (
              <span className="font-medium text-fg">{l.title}</span>
            )}
            {l.note && <p className="mt-0.5 text-[12px] text-fg-muted">{l.note}</p>}
          </li>
        );
      })}
    </ul>
  );
}
